import { useTranslation } from 'react-i18next';
import { useThemeContext } from '@utils/hooks/use-theme-context';
import PageHeader from '@components/layout/page-header';
import Tabs, { type Tab } from '@components/atoms/ui-kit/tabs';
import { Flag, Sliders, Languages, Settings, Zap } from 'lucide-react';

// Tab components
import LanguagesSettings from '@components/back-office/settings/languages-settings';
import FlagsSettings from '@components/back-office/settings/flags-settings';
import DefaultsSettings from '@components/back-office/settings/defaults-settings';

// Hooks
import { useGetSettingsOverview } from '@utils/api/back-office/settings';

export default function SettingsBo() {
  const { t } = useTranslation();
  const { getColour } = useThemeContext();

  const { data: overview, isLoading: overviewLoading } =
    useGetSettingsOverview();

  const OverviewCard = ({
    icon: Icon,
    label,
    value,
    colorClass,
    bgClass
  }: {
    icon: React.ElementType;
    label: string;
    value: number | undefined;
    colorClass: string;
    bgClass: string;
  }) => (
    <div className="rounded-2xl border border-hairline bg-app-soft shadow-card p-3 flex items-center gap-3">
      <div className={`p-2 rounded-full ${bgClass}`}>
        <Icon className={`h-5 w-5 ${colorClass}`} />
      </div>
      <div>
        {overviewLoading ? (
          <div
            className="h-6 w-12 rounded-2xl animate-pulse"
            style={{ backgroundColor: getColour('thirdLayer') }}
          />
        ) : (
          <p
            className="text-lg font-bold"
            style={{ color: getColour('normalText') }}
          >
            {value ?? 0}
          </p>
        )}
        <p className="text-xs" style={{ color: getColour('inactiveText') }}>
          {label}
        </p>
      </div>
    </div>
  );

  const tabs: Tab[] = [
    {
      id: 0,
      name: (
        <span className="flex items-center gap-2">
          <Languages className="h-4 w-4" />
          {t('languages')}
        </span>
      ),
      component: <LanguagesSettings />
    },
    {
      id: 1,
      name: (
        <span className="flex items-center gap-2">
          <Flag className="h-4 w-4" />
          {t('feature-flags')}
        </span>
      ),
      component: <FlagsSettings />
    },
    {
      id: 2,
      name: (
        <span className="flex items-center gap-2">
          <Sliders className="h-4 w-4" />
          {t('system-defaults')}
        </span>
      ),
      component: <DefaultsSettings />
    }
  ];

  return (
    <div className="bg-app flex-1 min-h-0 flex flex-col">
      {/* Header */}
      <div>
        <PageHeader
          title={t('settings')}
          caption={t('manage-system-configuration-and-defaults')}
        />
      </div>

      {/* Overview Cards */}
      <div className="py-4">
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          <OverviewCard
            icon={Languages}
            label={t('languages')}
            value={overview?.total_languages}
            colorClass="text-blue-500"
            bgClass="bg-blue-500/10"
          />
          <OverviewCard
            icon={Zap}
            label={t('feature-flags')}
            value={overview?.total_feature_flags}
            colorClass="text-violet-500"
            bgClass="bg-violet-500/10"
          />
          <OverviewCard
            icon={Settings}
            label={t('system-defaults')}
            value={overview?.total_system_defaults}
            colorClass="text-amber-500"
            bgClass="bg-amber-500/10"
          />
        </div>
      </div>

      {/* Tabs */}
      <div className="flex-1 min-h-0 overflow-hidden">
        <Tabs tabs={tabs} searchParams minHeight="auto" maxHeight="100%" />
      </div>
    </div>
  );
}
